import { acClient } from '@/clients/ac-client.js';
import { codesService, type Code } from '@/services/codes.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatAdminUserForm extends HatElement {
  private _userId: string | null = null;
  private _userTypes: Code[] = [];
  private _roles: Code[] = [];

  static get observedAttributes() {
    return ['user-id'];
  }

  attributeChangedCallback(name: string, _old: string | null, newVal: string | null) {
    if (name === 'user-id') this._userId = newVal;
  }

  async connectedCallback() {
    super.connectedCallback();
    this._userId = this.getAttribute('user-id');
    await this._loadCodes();
    this._renderShell();
    if (this._userId) await this._loadUser();
  }

  async _loadCodes() {
    try {
      [this._userTypes, this._roles] = await Promise.all([
        codesService.getDomain('ac.user.types'),
        codesService.getDomain('ac.user.roles'),
      ]);
    } catch {
      toastService.show('Failed to load code options.');
    }
  }

  async _loadUser() {
    try {
      const user = await acClient.getUserById(this._userId!);
      const emailDisplay = this.root.getElementById('email-display');
      if (emailDisplay) emailDisplay.textContent = user.email ?? '—';
      const f = this.root.querySelector('form') as any;
      f.name.value     = user.name     ?? '';
      f.timeZone.value = user.timeZone ?? '';
      f.userType.value = user.userType ?? '';

      const currentRoles = user.roles ?? [];
      this.root.querySelectorAll<HTMLInputElement>('input[data-role]').forEach(cb => {
        cb.checked = currentRoles.includes(cb.dataset.role!);
      });
    } catch {
      toastService.show('Failed to load user.');
    }
  }

  _renderShell() {
    const isEdit = !!this._userId;

    this.root.innerHTML = `
      <style>
        h2 {
          color: white;
          font-size: 1.4rem;
          margin: 0 0 24px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .back-link {
          display: inline-block;
          margin-bottom: 16px;
          color: rgba(255,255,255,0.8);
          cursor: pointer;
          font-size: 0.9rem;
          background: none;
          border: none;
          padding: 0;
          text-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }

        .back-link:hover { color: white; }

        .card {
          background: white;
          border-radius: 12px;
          padding: 28px;
          max-width: 480px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .field { margin-bottom: 18px; }

        label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #444;
          margin-bottom: 6px;
        }

        input[type="text"], input[type="email"], select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.95rem;
          box-sizing: border-box;
          outline: none;
        }

        input[type="text"]:focus, input[type="email"]:focus, select:focus {
          border-color: #764ba2;
        }

        .roles {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .role-check {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
          color: #333;
          cursor: pointer;
        }

        .section-divider {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #999;
          border-bottom: 1px solid #eee;
          padding-bottom: 6px;
          margin-bottom: 16px;
        }

        .actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .btn-primary {
          background: #764ba2;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.95rem;
        }

        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: default; }

        .btn-cancel {
          background: none;
          border: 1px solid #ddd;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.95rem;
          color: #555;
        }

        .btn-cancel:hover { background: #f5f5f5; }

        .email-display {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #eee;
        }

        .email-display span {
          font-weight: 600;
          color: #222;
        }
      </style>

      <button class="back-link">← Back to Users</button>
      <h2>${isEdit ? 'Edit User' : 'New User'}</h2>

      <div class="card">
        <form>
          ${!isEdit ? `
          <div class="field">
            <label for="email">Email</label>
            <input type="email" id="email" name="email" placeholder="user@example.com" />
          </div>
          ` : `
          <div class="email-display">Email: <span id="email-display">—</span></div>
          `}

          <div class="section-divider">Profile</div>

          <div class="field">
            <label for="name">Display Name</label>
            <input type="text" id="name" name="name" />
          </div>
          <div class="field">
            <label for="timeZone">Time Zone</label>
            <input type="text" id="timeZone" name="timeZone" placeholder="e.g. America/New_York" />
          </div>

          <div class="section-divider">Access</div>

          <div class="field">
            <label for="userType">User Type</label>
            <select id="userType" name="userType">
              ${this._userTypes.map(t => `<option value="${t.code}">${t.label}</option>`).join('')}
            </select>
          </div>

          <div class="field">
            <label>Roles</label>
            <div class="roles">
              ${this._roles.map(r => `
                <label class="role-check">
                  <input type="checkbox" data-role="${r.code}" />
                  ${r.label}
                </label>
              `).join('')}
            </div>
          </div>

          <div class="actions">
            <button type="submit" class="btn-primary" id="submit-btn">
              ${isEdit ? 'Save Changes' : 'Create User'}
            </button>
            <button type="button" class="btn-cancel" id="cancel-btn">Cancel</button>
          </div>
        </form>
      </div>
    `;

    this.root.querySelector('.back-link')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/users');
    });

    this.root.querySelector('#cancel-btn')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/users');
    });

    this.root.querySelector('form')!.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this._save();
    });
  }

  async _save() {
    const f = this.root.querySelector('form') as any;
    const submitBtn = this.root.querySelector<HTMLButtonElement>('#submit-btn')!;

    const selectedRoles = [...this.root.querySelectorAll<HTMLInputElement>('input[data-role]:checked')]
      .map(cb => cb.dataset.role!);

    const req = {
      name:     f.name.value.trim()     || undefined,
      timeZone: f.timeZone.value.trim() || undefined,
      userType: f.userType.value        || undefined,
      roles:    selectedRoles,
    };

    if (!this._userId) {
      const email = f.email?.value?.trim();
      if (!email) {
        toastService.show('Email is required.');
        return;
      }
      Object.assign(req, { email });
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    try {
      if (this._userId) {
        await acClient.updateUser(this._userId, req);
      } else {
        await acClient.createUser(req);
      }
      toastService.show(this._userId ? 'User updated.' : 'User created.', 'success');
      backboneService.navigateTo('/admin/users');
    } catch {
      toastService.show(this._userId ? 'Failed to update user.' : 'Failed to create user.');
      submitBtn.disabled = false;
      submitBtn.textContent = this._userId ? 'Save Changes' : 'Create User';
    }
  }
}

customElements.define('hat-admin-user-form', HatAdminUserForm);